import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black text-zinc-300">
      <div className="mx-auto max-w-5xl px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-zinc-400 transition hover:text-white"
          >
            ← Back to Career GPS AI
          </Link>

          <h1 className="mt-8 text-4xl font-bold tracking-tight text-white md:text-5xl">
            Privacy Policy
          </h1>

          <p className="mt-4 text-zinc-500">
            Last updated: September 11, 2026
          </p>
        </div>

        {/* Introduction */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            1. Introduction
          </h2>

          <p>
            Welcome to Career GPS AI. We respect your privacy and are committed
            to protecting the personal information you provide when using our
            website and services.
          </p>

          <p>
            This Privacy Policy explains what information we collect, how we
            use it, how we protect it, and the choices you may have regarding
            your information.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Information We Collect */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            2. Information We Collect
          </h2>

          <p>
            Depending on how you use Career GPS AI, we may collect information
            such as:
          </p>

          <ul className="list-disc space-y-2 pl-6">
            <li>Name and email address.</li>
            <li>Password information stored in securely hashed form.</li>
            <li>
              Information you provide while using career-related features.
            </li>
            <li>
              Information contained in documents or other content you choose
              to upload.
            </li>
            <li>
              Information you provide when communicating with our support
              team.
            </li>
            <li>
              Technical information such as browser type, device information,
              IP address, and basic usage information.
            </li>
          </ul>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* How We Use Information */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            3. How We Use Information
          </h2>

          <p>We may use collected information to:</p>

          <ul className="list-disc space-y-2 pl-6">
            <li>Create and maintain your Career GPS AI account.</li>
            <li>Authenticate your account and protect account security.</li>
            <li>Provide career-related tools and features.</li>
            <li>Generate AI-powered recommendations and insights.</li>
            <li>Process documents or information you choose to provide.</li>
            <li>Respond to support requests.</li>
            <li>Improve the performance and reliability of our services.</li>
            <li>Detect, prevent, and address security or technical issues.</li>
            <li>Comply with applicable legal obligations.</li>
          </ul>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Email Authentication */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            4. Email Authentication
          </h2>

          <p>
            If you create an account using your email address, we may use
            one-time verification codes (OTPs) to verify your email address
            and help protect your account.
          </p>

          <p>
            Verification codes are temporary and are intended to be used only
            for the authentication or account-recovery process for which they
            were issued.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Google and LinkedIn */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            5. Google and LinkedIn Sign-In
          </h2>

          <p>
            Career GPS AI may allow you to create or access an account using
            third-party authentication services such as Google or LinkedIn.
          </p>

          <p>
            If you choose to use these services, the applicable provider may
            share information with us according to your authorization and
            their privacy policies. We use the information received to
            authenticate your account and provide the requested service.
          </p>

          <p>
            We do not request access to third-party information that is not
            necessary for authentication unless clearly stated and authorized
            by you.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* AI Features */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            6. AI-Powered Features
          </h2>

          <p>
            Career GPS AI provides AI-powered career tools and recommendations.
            Information you submit to these features may be processed to
            generate responses, recommendations, analyses, or other requested
            results.
          </p>

          <p>
            You should avoid submitting sensitive personal information that is
            not necessary for the feature you are using.
          </p>

          <p>
            AI-generated information is provided for informational and career
            planning purposes and should not be treated as professional legal,
            financial, medical, or other regulated professional advice.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Information Sharing */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            7. Information Sharing
          </h2>

          <p>
            We do not sell your personal information.
          </p>

          <p>
            We may share information with service providers that help us
            operate Career GPS AI, such as hosting, database, authentication,
            email delivery, analytics, security, and AI service providers.
          </p>

          <p>
            These providers may process information only as necessary to
            provide services to us or as otherwise permitted by applicable
            law.
          </p>

          <p>
            We may also disclose information when required by law, legal
            process, or to protect the rights, safety, security, or property
            of Career GPS AI, our users, or others.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Cookies */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            8. Cookies and Similar Technologies
          </h2>

          <p>
            Career GPS AI may use cookies, browser storage, and similar
            technologies to maintain login sessions, remember preferences,
            improve functionality, and help secure the service.
          </p>

          <p>
            Depending on your browser and device settings, you may be able to
            control or disable certain cookies. Disabling some technologies
            may affect website functionality.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Security */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            9. Data Security
          </h2>

          <p>
            We use reasonable technical and organizational measures designed
            to protect personal information against unauthorized access,
            alteration, disclosure, or destruction.
          </p>

          <p>
            However, no internet transmission or electronic storage system can
            be guaranteed to be completely secure. You should use a strong,
            unique password and keep your account credentials confidential.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Data Retention */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            10. Data Retention
          </h2>

          <p>
            We retain personal information for as long as reasonably necessary
            to provide our services, maintain your account, fulfill the
            purposes described in this policy, resolve disputes, enforce
            agreements, and comply with legal obligations.
          </p>

          <p>
            Retention periods may vary depending on the type of information
            and the reason it was collected.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Rights */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            11. Your Choices and Rights
          </h2>

          <p>
            Depending on where you live and applicable privacy laws, you may
            have rights regarding your personal information, including the
            right to:
          </p>

          <ul className="list-disc space-y-2 pl-6">
            <li>Request access to certain personal information.</li>
            <li>Request correction of inaccurate information.</li>
            <li>Request deletion of certain information.</li>
            <li>Object to or restrict certain processing.</li>
            <li>Request a copy of certain information.</li>
            <li>Withdraw consent where processing is based on consent.</li>
          </ul>

          <p>
            To make a privacy-related request, contact us using the email
            address provided below.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Children's Privacy */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            12. Children's Privacy
          </h2>

          <p>
            Career GPS AI is not intended for children under the age required
            by applicable law to use the service independently. We do not
            knowingly collect personal information from children in violation
            of applicable laws.
          </p>

          <p>
            If you believe a child has provided personal information to us,
            please contact us so that we can review and take appropriate
            action.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Third Party Services */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            13. Third-Party Services
          </h2>

          <p>
            Career GPS AI may rely on third-party services to provide certain
            functionality, including hosting, authentication, email delivery,
            database infrastructure, AI processing, analytics, or other
            technical services.
          </p>

          <p>
            These third parties may process information according to their own
            privacy policies and applicable agreements.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* International Processing */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            14. International Data Processing
          </h2>

          <p>
            Depending on where you access Career GPS AI and where our service
            providers operate, your information may be processed or stored in
            countries other than your country of residence.
          </p>

          <p>
            Where required by applicable law, we will use appropriate
            safeguards for international transfers of personal information.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Changes */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold text-white">
            15. Changes to This Privacy Policy
          </h2>

          <p>
            We may update this Privacy Policy from time to time to reflect
            changes to our services, legal requirements, or privacy practices.
          </p>

          <p>
            When we make changes, we will update the "Last updated" date at the
            top of this page. Your continued use of Career GPS AI after an
            updated policy becomes effective means you acknowledge the updated
            policy, subject to applicable law.
          </p>
        </section>

        <hr className="my-10 border-zinc-800" />

        {/* Contact */}
        <section className="space-y-6">
          <h2 className="text-2xl font-semibold text-white">
            16. Contact Us
          </h2>

          <p>
            If you have questions, concerns, or requests regarding this Privacy
            Policy or how Career GPS AI handles your information, please
            contact us:
          </p>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-950/50 p-8">
            <h3 className="text-xl font-semibold text-white">
              Career GPS AI
            </h3>

            <p className="mt-4 text-zinc-400">
              Email:{" "}
              <a
                href="mailto:helpcareergpsai@gmail.com"
                className="text-blue-400 transition hover:text-blue-300"
              >
                helpcareergpsai@gmail.com
              </a>
            </p>
          </div>
        </section>

        {/* Footer */}
        <div className="mt-16 border-t border-zinc-800 pt-8 text-sm text-zinc-500">
          <p>
            © {new Date().getFullYear()} Career GPS AI. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
